// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { 
  AdManagementDashboard,
  AdNetworkConfig,
  AdFrequencyConfig,
  RewardedAdConfig,
  AdPlacementConfig,
  AdNetwork,
  AdType,
  Platform,
  FrequencyTrigger,
  AdPlacement,
  CreateAdNetworkRequest,
  UpdateAdNetworkRequest,
  CreateFrequencyConfigRequest,
  CreateRewardedAdRequest,
  CreateAdPlacementRequest,
  BackendAdType
} from '../../types/adManagement';
import { 
  AdsSettingsClass, 
  VirtualCurrencyClass, 
  AdUnitIDClass 
} from '../../classes/AdsSettings';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import UnsavedChangesModal from '../UnsavedChangesModal';
import './AdManagement.scss';

const AdManagement: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<AdManagementDashboard | null>(null);
  const [adsSettings, setAdsSettings] = useState<AdsSettingsClass | null>(null);
  const [originalSettings, setOriginalSettings] = useState<AdsSettingsClass | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'networks' | 'frequency' | 'rewards' | 'placements' | 'performance'>('networks');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['app_keys', 'rewarded', 'interstitial', 'banner']));

  // Form states
  const [networkForm, setNetworkForm] = useState<Partial<CreateAdNetworkRequest>>({
    network: AdNetwork.ADMOB,
    name: '',
    enabled: true,
    priority: 1,
    testMode: false,
    platformConfigs: {
      [Platform.ANDROID]: {
        appId: '',
        adUnits: {}
      },
      [Platform.IOS]: {
        appId: '',
        adUnits: {}
      }
    }
  });

  const [rewardForm, setRewardForm] = useState<Partial<CreateRewardedAdRequest>>({
    name: '',
    description: '',
    rewardType: 'coins',
    rewardAmount: 50,
    enabled: true,
    availableGames: [],
    cooldownMinutes: 5,
    maxRewardsPerDay: 10
  });

  // Fetch ad management data
  const fetchAdManagementData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await consoleService.getAdManagementDashboard();
      console.log(data)
      setDashboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ad management data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch ads configuration
  const fetchAdsConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const configData = await consoleService.getAdsConfig();
      // Handle both { body: string } and direct object responses
      const config = configData.body ? JSON.parse(configData.body) : configData;
      const settings = AdsSettingsClass.fromObject(config);
      setAdsSettings(settings);
      setOriginalSettings(settings.clone());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ads configuration');
    } finally {
      setLoading(false);
    }
  };

  // Save ads configuration
  const saveAdsConfig = async () => {
    if (!adsSettings) return;

    try {
      setSaving(true);
      const configData = adsSettings.toObject();
      await consoleService.updateAdsConfig(configData);
      setOriginalSettings(adsSettings.clone());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save ads configuration');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = originalSettings ? adsSettings?.hasChangedFrom(originalSettings) || false : false;

  const {
    showUnsavedChangesModal,
    isSaving,
    confirmLeave,
    saveAndLeave,
    cancelLeave
  } = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: saveAdsConfig,
    message: 'You have unsaved changes to the ads configuration. Do you want to save them before leaving?'
  });

  // Create ad network
  const handleCreateNetwork = async () => {
    try {
      await consoleService.createAdNetwork(networkForm as CreateAdNetworkRequest);
      setShowCreateModal(false);
      setNetworkForm({
        network: AdNetwork.ADMOB,
        name: '',
        enabled: true,
        priority: 1,
        testMode: false,
        platformConfigs: {
          [Platform.ANDROID]: { appId: '', adUnits: {} },
          [Platform.IOS]: { appId: '', adUnits: {} }
        }
      });
      await fetchAdManagementData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ad network');
    }
  };

  // Toggle network status
  const handleToggleNetwork = async (networkId: string, enabled: boolean) => {
    try {
      await consoleService.updateAdNetwork({ id: networkId, enabled });
      await fetchAdManagementData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update network');
    }
  };

  // Handle ad type toggle (rewarded, interstitial, banner) with auto-save
  const handleAdToggle = async (adType: 'rewarded' | 'interstitial' | 'banner', enabled: boolean) => {
    if (!adsSettings) return;

    // Update local state first for immediate UI feedback
    const updatedSettings = adsSettings.clone();
    updatedSettings[adType].enabled = enabled;
    setAdsSettings(updatedSettings);

    // Auto-save to server
    try {
      setSaving(true);
      setError(null);
      const configData = updatedSettings.toObject();
      await consoleService.updateAdsConfig(configData);
      setOriginalSettings(updatedSettings.clone());
      console.log(`[AdManagement] Successfully saved ${adType} enabled=${enabled} to server`);
    } catch (err) {
      // Revert local state on error
      setAdsSettings(adsSettings);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save ad configuration';
      setError(errorMessage);
      console.error(`[AdManagement] Failed to save ${adType} toggle:`, err);
      console.error(`[AdManagement] API endpoint may not be responding. Check server connection.`);
    } finally {
      setSaving(false);
    }
  };

  // Handle Play Frequency change with validation and auto-save on blur
  const handlePlayFrequencyChange = (value: string) => {
    if (!adsSettings) return;

    // Allow empty string for typing, but validate on blur
    const numValue = value === '' ? 0 : parseInt(value, 10);
    
    // Update local state immediately for responsive UI
    const updatedSettings = adsSettings.clone();
    updatedSettings.interstitial.play_frequency = isNaN(numValue) ? 1 : numValue;
    setAdsSettings(updatedSettings);
  };

  // Save Play Frequency on blur with validation
  const handlePlayFrequencyBlur = async () => {
    if (!adsSettings) return;

    const value = adsSettings.interstitial.play_frequency;

    // Validate: must be a positive integer
    if (!Number.isInteger(value) || value < 1) {
      setError('Play Frequency must be a positive integer (1 or greater)');
      // Reset to original value or default
      const updatedSettings = adsSettings.clone();
      updatedSettings.interstitial.play_frequency = originalSettings?.interstitial.play_frequency || 1;
      setAdsSettings(updatedSettings);
      console.error(`[AdManagement] Invalid play_frequency value: ${value}. Must be a positive integer.`);
      return;
    }

    // Auto-save to server
    try {
      setSaving(true);
      setError(null);
      const configData = adsSettings.toObject();
      await consoleService.updateAdsConfig(configData);
      setOriginalSettings(adsSettings.clone());
      console.log(`[AdManagement] Successfully saved play_frequency=${value} to server`);
    } catch (err) {
      // Revert local state on error
      if (originalSettings) {
        const revertedSettings = adsSettings.clone();
        revertedSettings.interstitial.play_frequency = originalSettings.interstitial.play_frequency;
        setAdsSettings(revertedSettings);
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to save play frequency';
      setError(errorMessage);
      console.error(`[AdManagement] Failed to save play_frequency:`, err);
      console.error(`[AdManagement] API endpoint may not be responding. Check server connection.`);
    } finally {
      setSaving(false);
    }
  };

  // Test ad network
  const handleTestNetwork = async (networkId: string, platform: Platform, adType: AdType) => {
    try {
      const result = await consoleService.testAdNetwork(networkId, platform, adType);
      alert(`Test ${result.success ? 'Successful' : 'Failed'}: ${result.message}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test ad network');
    }
  };

  useEffect(() => {
    if (activeTab === 'networks') {
      fetchAdsConfig();
    } else {
      fetchAdsConfig();
    }
  }, [activeTab]);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Handle field changes
  const handleFieldChange = (path: string, value: any) => {
    if (!adsSettings) return;

    const updatedSettings = adsSettings.clone();
    const parts = path.split('.');
    
    if (parts.length === 2) {
      // Handle app_keys.android, app_keys.ios
      if (parts[0] === 'app_keys') {
        if (parts[1] === 'android') {
          updatedSettings.app_keys.android = value;
        } else if (parts[1] === 'ios') {
          updatedSettings.app_keys.ios = value;
        }
      }
    } else if (parts.length === 1) {
      // Handle test_mode
      if (parts[0] === 'test_mode') {
        updatedSettings.test_mode = value;
      }
    } else if (parts.length === 3) {
      // Handle rewarded.enabled, rewarded.ad_unit_id.android, etc.
      if (parts[0] === 'rewarded') {
        if (parts[1] === 'enabled') {
          updatedSettings.rewarded.enabled = value;
        } else if (parts[1] === 'ad_unit_id') {
          if (parts[2] === 'android') {
            updatedSettings.rewarded.ad_unit_id.android = value;
          } else if (parts[2] === 'ios') {
            updatedSettings.rewarded.ad_unit_id.ios = value;
          }
        }
      } else if (parts[0] === 'interstitial') {
        if (parts[1] === 'enabled') {
          updatedSettings.interstitial.enabled = value;
        } else if (parts[1] === 'ad_unit_id') {
          if (parts[2] === 'android') {
            updatedSettings.interstitial.ad_unit_id.android = value;
          } else if (parts[2] === 'ios') {
            updatedSettings.interstitial.ad_unit_id.ios = value;
          }
        } else if (parts[1] === 'play_frequency') {
          updatedSettings.interstitial.play_frequency = value;
        }
      } else if (parts[0] === 'banner') {
        if (parts[1] === 'enabled') {
          updatedSettings.banner.enabled = value;
        } else if (parts[1] === 'ad_unit_id') {
          if (parts[2] === 'android') {
            updatedSettings.banner.ad_unit_id.android = value;
          } else if (parts[2] === 'ios') {
            updatedSettings.banner.ad_unit_id.ios = value;
          }
        }
      }
    }

    setAdsSettings(updatedSettings);
  };

  // Handle reward changes
  const handleRewardChange = (index: number, field: string, value: any) => {
    if (!adsSettings) return;

    const updatedSettings = adsSettings.clone();
    if (updatedSettings.rewarded.rewards[index]) {
      (updatedSettings.rewarded.rewards[index] as any)[field] = field === 'type' || field === 'amount' ? parseInt(value) : value;
    }
    setAdsSettings(updatedSettings);
  };

  // Add reward
  const addReward = () => {
    if (!adsSettings) return;

    const updatedSettings = adsSettings.clone();
    updatedSettings.rewarded.addReward(new VirtualCurrencyClass(2, 50));
    setAdsSettings(updatedSettings);
  };

  // Remove reward
  const removeReward = (index: number) => {
    if (!adsSettings) return;

    const updatedSettings = adsSettings.clone();
    updatedSettings.rewarded.removeReward(index);
    setAdsSettings(updatedSettings);
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  // Get network icon
  const getNetworkIcon = (network: AdNetwork): string => {
    switch (network) {
      case AdNetwork.ADMOB: return '🎯';
      case AdNetwork.UNITY_ADS: return '🎮';
      case AdNetwork.FACEBOOK_AUDIENCE: return '📘';
      case AdNetwork.IRONSOURCE: return '⚡';
      case AdNetwork.APPLOVIN: return '🚀';
      default: return '📺';
    }
  };

  // Get ad type icon
  const getAdTypeIcon = (adType: AdType): string => {
    switch (adType) {
      case AdType.INTERSTITIAL: return '📱';
      case AdType.REWARDED: return '🎁';
      case AdType.BANNER: return '📊';
      case AdType.NATIVE: return '📄';
      default: return '📺';
    }
  };

  if (loading) {
    return (
      <div className="ad-management-container">
        <div className="ad-management-header">
          <h2 className="ad-management-title">Ad Management</h2>
        </div>
        <div className="loading-state">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading ad management...</span>
          </div>
          <p>Loading ad management data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ad-management-container">
        <div className="ad-management-header">
          <h2 className="ad-management-title">Ad Management</h2>
        </div>
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <button className="btn btn-primary ms-3" onClick={fetchAdManagementData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!adsSettings) {
    return (
      <div className="ad-management-container">
        <div className="ad-management-header">
          <h2 className="ad-management-title">Ad Management</h2>
        </div>
        <div className="alert alert-info">
          No ad management data available.
        </div>
      </div>
    );
  }

  return (
    <div className="ad-management-container">
      {/* Header */}
      <div className="ad-management-header">
        <h2 className="ad-management-title">📺 Ad Management</h2>
      </div>

      {/* Summary Cards */}

      {/* Tab Navigation */}

      {/* Tab Content */}
      <div className="tab-content">
        {/* Networks Tab */}
        {activeTab === 'networks' && (
          <div className="networks-tab">
            {loading && !adsSettings ? (
              <div className="loading-state">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading ads configuration...</span>
                </div>
                <p>Loading ads configuration...</p>
              </div>
            ) : !adsSettings ? (
              <div className="alert alert-info">
                No ads configuration available.
              </div>
            ) : (
              <>
                <div className="section-header">
                  <div className="header-content">
                    <h3>Configuration</h3>
                  </div>
                  <div className="header-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={saveAdsConfig}
                      disabled={saving || !hasUnsavedChanges}
                    >
                      {saving ? '💾 Saving...' : '💾 Save Configuration'}
                    </button>
                  </div>
                </div>

                {/* App Keys Section */}
                <div className="config-section">
                  <div className="config-section-header" onClick={() => toggleSection('app_keys')}>
                    <h4>🔑 App Keys</h4>
                    <span className="expand-icon">
                      {expandedSections.has('app_keys') ? '▼' : '▶'}
                    </span>
                  </div>
                  {expandedSections.has('app_keys') && (
                    <div className="config-section-content">
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Android App Key:</label>
                          <input
                            type="text"
                            value={adsSettings.app_keys.android}
                            onChange={(e) => handleFieldChange('app_keys.android', e.target.value)}
                            className="form-control"
                            placeholder="Enter Android app key"
                          />
                        </div>
                        <div className="form-group">
                          <label>iOS App Key:</label>
                          <input
                            type="text"
                            value={adsSettings.app_keys.ios}
                            onChange={(e) => handleFieldChange('app_keys.ios', e.target.value)}
                            className="form-control"
                            placeholder="Enter iOS app key"
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={adsSettings.test_mode}
                              onChange={(e) => handleFieldChange('test_mode', e.target.checked)}
                            />
                            Test Mode
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Rewarded Ad Section */}
                <div className="config-section">
                  <div className="config-section-header" onClick={() => toggleSection('rewarded')}>
                    <h4>🎁 Rewarded Ad</h4>
                    <div className="section-badges">
                      <span className={`status-badge ${adsSettings.rewarded.enabled ? 'enabled' : 'disabled'}`}>
                        {adsSettings.rewarded.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <span className="expand-icon">
                      {expandedSections.has('rewarded') ? '▼' : '▶'}
                    </span>
                  </div>
                  {expandedSections.has('rewarded') && (
                    <div className="config-section-content">
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="toggle-label">
                            <input
                              type="checkbox"
                              checked={adsSettings.rewarded.enabled}
                              onChange={(e) => handleAdToggle('rewarded', e.target.checked)}
                              disabled={saving}
                            />
                            {saving ? 'Saving...' : 'Enabled'}
                          </label>
                        </div>
                        <div className="form-group">
                          <label>Android Ad Unit ID:</label>
                          <input
                            type="text"
                            value={adsSettings.rewarded.ad_unit_id.android}
                            onChange={(e) => handleFieldChange('rewarded.ad_unit_id.android', e.target.value)}
                            className="form-control"
                            placeholder="Enter Android ad unit ID"
                          />
                        </div>
                        <div className="form-group">
                          <label>iOS Ad Unit ID:</label>
                          <input
                            type="text"
                            value={adsSettings.rewarded.ad_unit_id.ios}
                            onChange={(e) => handleFieldChange('rewarded.ad_unit_id.ios', e.target.value)}
                            className="form-control"
                            placeholder="Enter iOS ad unit ID"
                          />
                        </div>
                      </div>

                      {/* Rewards */}
                      <div className="rewards-section">
                        <div className="rewards-header">
                          <h5>Rewards</h5>
                          <button 
                            className="btn btn-success btn-sm"
                            onClick={addReward}
                          >
                            ➕ Add Reward
                          </button>
                        </div>
                        <div className="rewards-list">
                          {adsSettings.rewarded.rewards.map((reward, index) => (
                            <div key={index} className="reward-item">
                              <div className="reward-fields">
                                <div className="form-group">
                                  <label>Currency Type:</label>
                                  <select
                                    value={reward.type}
                                    onChange={(e) => handleRewardChange(index, 'type', parseInt(e.target.value))}
                                    className="form-control"
                                  >
                                    <option value={1}>Coin</option>
                                    <option value={2}>Ludo Coin</option>
                                    <option value={3}>Quiz Coin</option>
                                    <option value={4}>Solitaire Coin</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>Amount:</label>
                                  <input
                                    type="number"
                                    value={reward.amount}
                                    onChange={(e) => handleRewardChange(index, 'amount', parseInt(e.target.value))}
                                    className="form-control"
                                    min="1"
                                  />
                                </div>
                              </div>
                              {adsSettings.rewarded.rewards.length > 1 && (
                                <button 
                                  className="btn btn-danger btn-sm"
                                  onClick={() => removeReward(index)}
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Interstitial Ad Section */}
                <div className="config-section">
                  <div className="config-section-header" onClick={() => toggleSection('interstitial')}>
                    <h4>📱 Interstitial Ad</h4>
                    <div className="section-badges">
                      <span className={`status-badge ${adsSettings.interstitial.enabled ? 'enabled' : 'disabled'}`}>
                        {adsSettings.interstitial.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <span className="expand-icon">
                      {expandedSections.has('interstitial') ? '▼' : '▶'}
                    </span>
                  </div>
                  {expandedSections.has('interstitial') && (
                    <div className="config-section-content">
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="toggle-label">
                            <input
                              type="checkbox"
                              checked={adsSettings.interstitial.enabled}
                              onChange={(e) => handleAdToggle('interstitial', e.target.checked)}
                              disabled={saving}
                            />
                            {saving ? 'Saving...' : 'Enabled'}
                          </label>
                        </div>
                        <div className="form-group">
                          <label>Android Ad Unit ID:</label>
                          <input
                            type="text"
                            value={adsSettings.interstitial.ad_unit_id.android}
                            onChange={(e) => handleFieldChange('interstitial.ad_unit_id.android', e.target.value)}
                            className="form-control"
                            placeholder="Enter Android ad unit ID"
                          />
                        </div>
                        <div className="form-group">
                          <label>iOS Ad Unit ID:</label>
                          <input
                            type="text"
                            value={adsSettings.interstitial.ad_unit_id.ios}
                            onChange={(e) => handleFieldChange('interstitial.ad_unit_id.ios', e.target.value)}
                            className="form-control"
                            placeholder="Enter iOS ad unit ID"
                          />
                        </div>
                        <div className="form-group">
                          <label>Play Frequency (after x matches):</label>
                          <input
                            type="number"
                            value={adsSettings.interstitial.play_frequency}
                            onChange={(e) => handlePlayFrequencyChange(e.target.value)}
                            onBlur={handlePlayFrequencyBlur}
                            className="form-control"
                            min="1"
                            disabled={saving}
                            placeholder="Enter positive integer"
                          />
                          {saving && <small className="text-muted">Saving...</small>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Banner Ad Section */}
                <div className="config-section">
                  <div className="config-section-header" onClick={() => toggleSection('banner')}>
                    <h4>📊 Banner Ad</h4>
                    <div className="section-badges">
                      <span className={`status-badge ${adsSettings.banner.enabled ? 'enabled' : 'disabled'}`}>
                        {adsSettings.banner.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <span className="expand-icon">
                      {expandedSections.has('banner') ? '▼' : '▶'}
                    </span>
                  </div>
                  {expandedSections.has('banner') && (
                    <div className="config-section-content">
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="toggle-label">
                            <input
                              type="checkbox"
                              checked={adsSettings.banner.enabled}
                              onChange={(e) => handleAdToggle('banner', e.target.checked)}
                              disabled={saving}
                            />
                            {saving ? 'Saving...' : 'Enabled'}
                          </label>
                        </div>
                        <div className="form-group">
                          <label>Android Ad Unit ID:</label>
                          <input
                            type="text"
                            value={adsSettings.banner.ad_unit_id.android}
                            onChange={(e) => handleFieldChange('banner.ad_unit_id.android', e.target.value)}
                            className="form-control"
                            placeholder="Enter Android ad unit ID"
                          />
                        </div>
                        <div className="form-group">
                          <label>iOS Ad Unit ID:</label>
                          <input
                            type="text"
                            value={adsSettings.banner.ad_unit_id.ios}
                            onChange={(e) => handleFieldChange('banner.ad_unit_id.ios', e.target.value)}
                            className="form-control"
                            placeholder="Enter iOS ad unit ID"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Unsaved Changes Modal */}
                <UnsavedChangesModal
                  isOpen={showUnsavedChangesModal}
                  message="You have unsaved changes to the ads configuration. Do you want to save them before leaving?"
                  onSaveAndLeave={saveAndLeave}
                  onLeaveWithoutSaving={confirmLeave}
                  onCancel={cancelLeave}
                  isSaving={isSaving}
                  showSaveOption={true}
                />
              </>
            )}
          </div>
        )}

        {/* Frequency Tab */}
        {activeTab === 'frequency' && (
          <div></div>
        )}

      </div>

      {/* Create Network Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Ad Network</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Network Type:</label>
                <select 
                  value={networkForm.network} 
                  onChange={(e) => setNetworkForm({...networkForm, network: e.target.value as AdNetwork})}
                  className="form-control"
                >
                  {Object.values(AdNetwork).map(network => (
                    <option key={network} value={network}>
                      {getNetworkIcon(network)} {network.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Network Name:</label>
                <input
                  type="text"
                  value={networkForm.name}
                  onChange={(e) => setNetworkForm({...networkForm, name: e.target.value})}
                  className="form-control"
                  placeholder="e.g., AdMob Primary"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority:</label>
                  <input
                    type="number"
                    value={networkForm.priority}
                    onChange={(e) => setNetworkForm({...networkForm, priority: parseInt(e.target.value)})}
                    className="form-control"
                    min="1"
                    max="10"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={networkForm.testMode}
                      onChange={(e) => setNetworkForm({...networkForm, testMode: e.target.checked})}
                    />
                    Test Mode
                  </label>
                </div>
              </div>

              <div className="platform-configs">
                <h4>Platform Configuration</h4>
                
                <div className="platform-section">
                  <h5>🤖 Android</h5>
                  <input
                    type="text"
                    placeholder="Android App ID"
                    value={networkForm.platformConfigs?.android.appId || ''}
                    onChange={(e) => setNetworkForm({
                      ...networkForm,
                      platformConfigs: {
                        ...networkForm.platformConfigs!,
                        android: {
                          ...networkForm.platformConfigs!.android,
                          appId: e.target.value
                        }
                      }
                    })}
                    className="form-control"
                  />
                </div>

                <div className="platform-section">
                  <h5>🍎 iOS</h5>
                  <input
                    type="text"
                    placeholder="iOS App ID"
                    value={networkForm.platformConfigs?.ios.appId || ''}
                    onChange={(e) => setNetworkForm({
                      ...networkForm,
                      platformConfigs: {
                        ...networkForm.platformConfigs!,
                        ios: {
                          ...networkForm.platformConfigs!.ios,
                          appId: e.target.value
                        }
                      }
                    })}
                    className="form-control"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreateNetwork}>
                Create Network
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdManagement;
